/*
  The single door to the backend.

  Everything the application reads or writes goes through here, so there is one
  place that knows about the access token, the tenant header, refresh, and how
  the API reports failure. Screens never call fetch directly.
*/

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly fieldErrors?: Record<string, string[]>;
  readonly traceId?: string;

  constructor(status: number, problem?: ProblemDetails, fallback?: string) {
    super(
      problem?.detail ||
        problem?.title ||
        fallback ||
        `The request failed (HTTP ${status}).`,
    );

    this.name = "ApiError";
    this.status = status;
    this.code = problem?.code;
    this.fieldErrors = problem?.errors;
    this.traceId = problem?.traceId;
  }

  /** True when the caller is signed out, as opposed to merely not permitted. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** True when another person changed the record first. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** Field errors flattened for display above a form. */
  get validationSummary(): string[] {
    if (!this.fieldErrors) return [];
    return Object.entries(this.fieldErrors).flatMap(([field, messages]) =>
      messages.map((m) => (field.startsWith("$") ? m : `${label(field)}: ${m}`)),
    );
  }
}

function label(field: string): string {
  const name = field.split(".").pop() ?? field;
  return name.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

const BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";
const TOKEN_KEY = "wolfray.session";

/** Refresh this long before the access token expires, so a request never races it. */
const REFRESH_MARGIN_MS = 60_000;

export interface StoredSession {
  accessToken: string;
  refreshToken?: string;
  expiresAtUtc: string;
  tenantSlug?: string;
}

let session: StoredSession | null = readSession();
let onUnauthenticated: (() => void) | null = null;
let refreshing: Promise<boolean> | null = null;

function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    // A blocked or corrupt store means "not signed in", never a crash.
    return null;
  }
}

export function getSession(): StoredSession | null {
  return session;
}

export function setSession(next: StoredSession | null): void {
  session = next;

  try {
    if (next) {
      localStorage.setItem(TOKEN_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // Storage is a convenience. The in-memory session still works for this tab.
  }
}

// Another tab may refresh or sign out. Following it means this tab never presents
// a refresh token the other tab has already exchanged, which the server would refuse.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === TOKEN_KEY) session = readSession();
  });
}

/** Registered by the auth provider so a 401 anywhere returns the user to sign-in. */
export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  /** Skips the automatic sign-out on 401 — used by the sign-in call itself. */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const url = `${BASE}${path.startsWith("/") ? path : `/${path}`}`;

  if (!query) return url;

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null && item !== "") {
          params.append(key, String(item));
        }
      }
    } else {
      params.append(key, String(value));
    }
  }

  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseProblem(response: Response): Promise<ProblemDetails | undefined> {
  const type = response.headers.get("content-type") ?? "";

  if (!type.includes("json")) return undefined;

  try {
    return (await response.json()) as ProblemDetails;
  } catch {
    return undefined;
  }
}

function authHeaders(anonymous?: boolean): Record<string, string> {
  const headers: Record<string, string> = {};

  if (session?.accessToken && !anonymous) {
    headers.Authorization = `Bearer ${session.accessToken}`;
  }

  if (session?.tenantSlug) {
    headers["X-Tenant"] = session.tenantSlug;
  }

  return headers;
}

function expiresSoon(value: StoredSession): boolean {
  const expires = Date.parse(value.expiresAtUtc);
  return Number.isFinite(expires) && expires - Date.now() < REFRESH_MARGIN_MS;
}

function endSession(): void {
  setSession(null);
  onUnauthenticated?.();
}

/**
 * Exchanges the refresh token for a new session.
 *
 * Access tokens are deliberately short-lived; the refresh token is what keeps a
 * working session alive. Concurrent callers share a single exchange, because the
 * server rotates the refresh token and a second exchange with the old one fails.
 */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;

  const current = session;
  if (!current?.refreshToken) return Promise.resolve(false);

  const attempt = (async () => {
    try {
      // Another tab may already have exchanged it; adopt that session instead.
      const stored = readSession();
      if (stored?.accessToken && stored.accessToken !== current.accessToken) {
        session = stored;
        return true;
      }

      const response = await fetch(buildUrl("/auth/refresh"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(current.tenantSlug ? { "X-Tenant": current.tenantSlug } : {}),
        },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });

      if (!response.ok) return false;

      const result = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
        expiresAtUtc: string;
        user?: { tenant?: { slug?: string } };
      };

      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAtUtc: result.expiresAtUtc,
        tenantSlug: result.user?.tenant?.slug ?? current.tenantSlug,
      });

      return true;
    } catch {
      return false;
    }
  })();

  refreshing = attempt;
  void attempt.finally(() => {
    if (refreshing === attempt) refreshing = null;
  });

  return attempt;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal, anonymous } = options;

  // Refresh just ahead of expiry rather than waiting for a refusal.
  if (!anonymous && session?.refreshToken && expiresSoon(session)) {
    await refreshSession();
  }

  const send = () => {
    const headers: Record<string, string> = { Accept: "application/json", ...authHeaders(anonymous) };

    if (body !== undefined && !(body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    return fetch(buildUrl(path, query), {
      method,
      headers,
      signal,
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
    });
  };

  let response = await send();

  if (response.status === 401 && !anonymous) {
    // An expired access token is routine: exchange the refresh token and try once
    // more. The first attempt was refused before it ran, so retrying repeats nothing.
    if (session?.refreshToken && (await refreshSession())) {
      response = await send();
    }

    if (response.status === 401) {
      // Now the session really has ended; every screen should see signed-out.
      endSession();
      throw new ApiError(401, await parseProblem(response), "Your session has ended.");
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, await parseProblem(response));
  }

  if (response.status === 204) return undefined as T;

  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("json")) return undefined as T;

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),

  post: <T>(path: string, body?: unknown, query?: Record<string, unknown>) =>
    request<T>(path, { method: "POST", body, query }),

  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),

  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),

  /** Multipart upload; the browser sets the boundary itself. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),

  /**
   * Downloads a file through the authenticated client, so protected documents
   * work the same way as any other request. Returns a blob the caller saves, and
   * the row count a report export states in X-Row-Count (null for other files).
   */
  async download(path: string): Promise<{ blob: Blob; fileName: string; rowCount: number | null }> {
    if (session?.refreshToken && expiresSoon(session)) {
      await refreshSession();
    }

    const send = () => fetch(buildUrl(path), { headers: authHeaders() });

    let response = await send();

    if (response.status === 401 && session?.refreshToken && (await refreshSession())) {
      response = await send();
    }

    if (response.status === 401) {
      endSession();
      throw new ApiError(401, await parseProblem(response), "Your session has ended.");
    }

    if (!response.ok) {
      throw new ApiError(response.status, await parseProblem(response));
    }

    const disposition = response.headers.get("content-disposition") ?? "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const rows = Number.parseInt(response.headers.get("x-row-count") ?? "", 10);

    return {
      blob: await response.blob(),
      fileName: match ? decodeURIComponent(match[1]) : "download",
      rowCount: Number.isFinite(rows) ? rows : null,
    };
  },
};

/** Saves a downloaded blob to the user's machine. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ Paging */

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export const emptyPage = <T>(): PagedResult<T> => ({
  items: [],
  page: 1,
  pageSize: 25,
  totalCount: 0,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false,
});
